import { Banner, BlockStack, Box, Button, InlineStack, Text } from '@shopify/polaris';
import { Link, useNavigate, useParams } from 'react-router';
import { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';

/**
 * PurchaseOrderASNDetails Component
 *
 * Displays the details of a purchase order's Advanced Shipping Notice (ASN)
 * and provides functionality to create receiving documents.
 *
 * @param {Object} props - Component props
 * @param {Object} props.parentData - Parent data object containing ASN details
 * @returns {JSX.Element} The rendered component
 */
const PurchaseOrderASNDetails = ({ parentData }) => {
    // Router hooks
    const navigate = useNavigate();
    const { poId, asnId } = useParams();

    // Consolidated state management
    const [state, setState] = useState({
        loading: false,
        error: '',
        success: '',
    });

    // Destructure state for easier access
    const { loading } = state;

    /**
     * Memoized API endpoint URL
     */
    const apiEndpoint = useMemo(() => {
        return `${import.meta.env.VITE_API_URL}/api/app/purchase-orders/${poId}/advanced-shipping-notice/${asnId}`;
    }, [poId, asnId]);

    /**
     * Clears any existing error or success messages
     */
    const clearMessages = useCallback(() => {
        setState((prev) => ({ ...prev, error: '', success: '' }));
    }, []);

    /**
     * Creates a new ASN receiving document
     * Makes an API call and navigates to the new document on success
     */
    const handleCreateASNReceivingDocument = useCallback(async () => {
        // Clear any existing messages
        clearMessages();

        // Set loading state
        setState((prev) => ({ ...prev, loading: true }));

        try {
            // Make API request to create receiving document
            const response = await axios.post(
                apiEndpoint,
                { action: 'create-asn-receiving-document' },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            const { data } = response.data;

            // Navigate to the new receiving document if creation was successful
            if (response.status === 200 && data.receivingDocument) {
                // Prepare navigation state
                const navigationState = {
                    status: '',
                    poId: parentData.netsuiteId,
                    asnId: parentData.id,
                    asnNetsuiteId: parentData.asnNetsuiteId || parentData.netsuiteId,
                    parentData: parentData,
                };

                // Navigate to the receiving document page
                navigate(`/purchase-orders/${poId}/advanced-shipping-notice/${asnId}/receiving-document/${data.receivingDocument.rdId}`, { state: navigationState });

                // Set success message
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    success: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_ASN.PURCHASE_ORDER_ASN_DETAILS.ASN_RECEIVING_DOCUMENT_CREATION_SUCCESS,
                }));
            }
        } catch (error) {
            // Set error state with appropriate message
            setState((prev) => ({
                ...prev,
                loading: false,
                error: error.response?.data?.message || PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_ASN.PURCHASE_ORDER_ASN_DETAILS.ASN_RECEIVING_DOCUMENT_CREATION_ERROR,
            }));
        }
    }, [apiEndpoint, asnId, clearMessages, navigate, parentData, poId]);

    return (
        <>
            <BlockStack>
                <Box>
                    <Text variant="headingLg">{parentData?.asnNetsuiteId || asnId}</Text>
                </Box>
                <Box style={{ marginTop: '1rem' }}>
                    <Box>
                        {parentData?.auditRequired === true ? (
                            <Box style={{ width: '250px', marginBottom: '1rem' }}>
                                <Banner title={PURCHASE_ORDER_STRINGS.AUDIT_REQUIRED} tone="critical" width={'50px'} />
                            </Box>
                        ) : (
                            <> </>
                        )}
                        <Box>
                            <Text>
                                <b>{PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_ASN.PURCHASE_ORDER_ASN_DETAILS.SHIPMENT_ID}: </b>
                                {poId}
                            </Text>
                            <Text>
                                <b>{PURCHASE_ORDER_STRINGS.PURCHASE_ORDER}: </b>
                                <Link
                                    style={{ color: '#005BD3' }}
                                    onClick={() => {
                                        navigate(`/purchase-orders/${poId}`);
                                    }}
                                >
                                    {parentData?.netSuiteHumanPoNumber}
                                </Link>
                            </Text>
                            <Text>
                                <b>{PURCHASE_ORDER_STRINGS.EXPECTED_DATE}: </b>
                                {parentData?.expectedDate}
                            </Text>
                            <Text>
                                <b>{PURCHASE_ORDER_STRINGS.CARTONS}: </b>
                                {parentData?.totalCartons}
                            </Text>
                        </Box>
                    </Box>
                </Box>
            </BlockStack>
            {parentData?.status !== 'Closed' ? (
                <InlineStack align="end">
                    <Button variant="primary" loading={loading} onClick={handleCreateASNReceivingDocument}>
                        {PURCHASE_ORDER_STRINGS.RECEIVE}
                    </Button>
                </InlineStack>
            ) : (
                <></>
            )}
        </>
    );
};

export default PurchaseOrderASNDetails;
